import { useQuery } from '@tanstack/react-query'
import {
  Building2,
  CheckCircle2,
  GraduationCap,
  MailQuestion,
  PauseCircle,
  Plus,
  UsersRound,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { StatCard } from '@/components/ui/StatCard'
import { overviewQuery, schoolsQuery } from '@/features/platform/api'
import { CreateSchoolDialog } from '@/features/platform/components/CreateSchoolDialog'
import { SchoolStatusBadge } from '@/features/platform/components/StatusBadge'
import type { PlatformSchool, SchoolStatus } from '@/features/platform/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate, formatDateTime, formatNumber } from '@/utils/format'

const STATUSES: { value: SchoolStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Awaiting administrator' },
  { value: 'suspended', label: 'Suspended' },
]

export function PlatformSchoolsPage() {
  const { t } = useTranslation('platform')
  useDocumentTitle('Schools')
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [creating, setCreating] = useState(false)
  const search = params.get('search') ?? ''
  const rawStatus = params.get('status')
  const status = STATUSES.some((s) => s.value === rawStatus) ? (rawStatus as SchoolStatus) : undefined
  const page = Math.max(1, Number(params.get('page')) || 1)
  const overview = useQuery(overviewQuery())
  const list = useQuery(schoolsQuery({ search: search || undefined, status, page }))

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    if (!('page' in patch)) next.delete('page')
    setParams(next, { replace: true })
  }

  const columns = useMemo<Column<PlatformSchool>[]>(
    () => [
      {
        key: 'school',
        header: t('School'),
        cell: (s) => (
          <div className="flex items-center gap-3">
            <Avatar name={s.name} src={s.logoUrl} size="sm" />
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{s.name}</p>
              <p className="truncate text-xs text-muted">
                {s.url
                  ? s.url.replace(/^https?:\/\//, '')
                  : [s.countryCode, s.timezone].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        ),
      },
      {
        key: 'admin',
        header: t('Administrator'),
        hideBelow: 'lg',
        cell: (s) =>
          s.admin ? (
            <div className="min-w-0">
              <p className="truncate">{s.admin.name}</p>
              <p className="truncate text-xs text-muted">{s.admin.email}</p>
            </div>
          ) : (
            '—'
          ),
      },
      { key: 'status', header: t('Status'), cell: (s) => <SchoolStatusBadge status={s.status} /> },
      {
        key: 'students',
        header: t('Students'),
        align: 'right',
        hideBelow: 'md',
        cell: (s) => <span className="tabular">{formatNumber(s.counts.students)}</span>,
      },
      {
        key: 'staff',
        header: t('Staff'),
        align: 'right',
        hideBelow: 'md',
        cell: (s) => <span className="tabular">{formatNumber(s.counts.staff)}</span>,
      },
      {
        key: 'activity',
        header: t('Last sign-in'),
        hideBelow: 'xl',
        cell: (s) =>
          s.lastActivityAt ? formatDateTime(s.lastActivityAt) : <span className="text-muted">—</span>,
      },
      { key: 'created', header: t('Created'), hideBelow: 'lg', cell: (s) => formatDate(s.createdAt) },
    ],
    [t],
  )

  const o = overview.data
  return (
    <>
      <PageHeader
        title={t('Schools')}
        description={t('Every school on the platform. Create one to invite its administrator.')}
        actions={
          <Button leftIcon={<Plus />} onClick={() => setCreating(true)}>
            {t('Create a school')}
          </Button>
        }
      />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('Schools')}
          value={o ? formatNumber(o.schools) : '—'}
          icon={<Building2 />}
          loading={overview.isPending}
          hint={o ? t('{{count}} new this month', { count: o.newThisMonth }) : undefined}
        />
        <StatCard
          label={t('Active')}
          value={o ? formatNumber(o.active) : '—'}
          icon={<CheckCircle2 />}
          tone="success"
          loading={overview.isPending}
          hint={
            o
              ? [
                  t('{{count}} awaiting administrator', { count: o.invited }),
                  t('{{count}} suspended', { count: o.suspended }),
                ].join(' · ')
              : undefined
          }
        />
        <StatCard
          label={t('Active students')}
          value={o ? formatNumber(o.students) : '—'}
          icon={<GraduationCap />}
          tone="violet"
          loading={overview.isPending}
        />
        <StatCard
          label={t('Active staff')}
          value={o ? formatNumber(o.staff) : '—'}
          icon={<UsersRound />}
          tone="info"
          loading={overview.isPending}
          hint={o ? t('{{count}} user accounts', { count: o.users }) : undefined}
        />
      </div>
      <Card>
        <FilterBar
          actions={
            search || status ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => update({ search: undefined, status: undefined })}
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={search}
            onChange={(v) => update({ search: v || undefined })}
            placeholder={t('School name or administrator email')}
            label={t('Search schools')}
          />
          <Select
            aria-label={t('Filter by status')}
            className="sm:w-auto sm:min-w-52"
            placeholder={t('Any status')}
            value={status ?? ''}
            onChange={(e) => update({ status: e.target.value || undefined })}
            options={STATUSES.map((s) => ({ value: s.value, label: t(s.label) }))}
          />
        </FilterBar>
        <DataTable
          caption={t('Schools')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(s) => s.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          onRowClick={(s) => void navigate(`/platform/schools/${s.id}`)}
          mobileCard={(s) => (
            <div className="flex items-center gap-3">
              <Avatar name={s.name} src={s.logoUrl} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{s.name}</p>
                <p className="truncate text-xs text-muted">
                  {s.admin?.email ?? '—'} · {t('{{count}} students', { count: s.counts.students })}
                </p>
              </div>
              <SchoolStatusBadge status={s.status} />
            </div>
          )}
          empty={
            <EmptyState
              compact
              icon={search || status ? <MailQuestion /> : <PauseCircle />}
              title={search || status ? t('No school matches these filters') : t('No school yet')}
              description={
                search || status ? t('Try a different search.') : t('Create the first school to get started.')
              }
            />
          }
        />
        <Pagination meta={list.data?.meta} onPageChange={(p) => update({ page: String(p) })} />
      </Card>
      {creating ? <CreateSchoolDialog onOpenChange={setCreating} /> : null}
    </>
  )
}
